"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Calendar, ArrowRight, CircleAlert } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const formatDate = (dateString?: string) => {
  if (!dateString) return "Date TBA";
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  }).format(date);
};
function EventsGridConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(3)].map((_, i) => <Card key={i} data-index={i} className="border border-border shadow-none rounded-sm h-[350px] animate-pulse bg-muted/20">
            <CardHeader className="pb-4">
              <div className="h-6 w-24 bg-muted rounded-sm mb-4" />
              <div className="h-6 w-3/4 bg-muted rounded-sm mb-2" />
              <div className="h-6 w-1/2 bg-muted rounded-sm" />
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="h-4 w-full bg-muted rounded-sm" />
                <div className="h-4 w-full bg-muted rounded-sm" />
                <div className="h-4 w-2/3 bg-muted rounded-sm" />
              </div>
            </CardContent>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center py-12 text-center border border-border rounded-sm bg-muted/10">
        <CircleAlert className="size-10 text-destructive mb-4" />
        <p className="text-lg font-medium text-foreground mb-2">Failed to load events</p>
        <p className="text-sm text-muted-foreground mb-4 max-w-md">
          We encountered an issue while retrieving the upcoming events schedule.
        </p>
        <Button variant="outline" onClick={refetch} className="rounded-sm">
          Try Again
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="flex flex-col items-center justify-center py-16 text-center border border-border rounded-sm bg-muted/5">
        <SafeHydrate><Calendar className="size-12 text-muted-foreground mb-4 opacity-50" /></SafeHydrate>
        <h3 className="text-xl font-semibold font-default text-foreground mb-2">No Upcoming Events</h3>
        <p className="text-muted-foreground max-w-md">
          There are currently no scheduled events. Please check back later or subscribe to our newsletter for updates.
        </p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {posts.map((post, i) => <Card key={post.id} data-index={i} className="flex flex-col h-full border border-border shadow-none rounded-sm transition-colors duration-300 hover:border-primary/60 bg-card">
          <CardHeader className="pb-4">
            <div className="mb-4">
              <Badge variant="default" className="rounded-sm px-3 py-1.5 text-xs font-mono uppercase tracking-wider bg-primary text-primary-foreground">
                {formatDate(post.date)}
              </Badge>
            </div>
            <CardTitle className="text-xl font-default font-semibold leading-tight text-foreground line-clamp-2">
              {post.title}
            </CardTitle>
          </CardHeader>
          
          <CardContent className="flex-grow">
            <CardDescription className="text-base text-muted-foreground line-clamp-3">
              {post.excerpt ? <span dangerouslySetInnerHTML={{
            __html: post.excerpt
          }} /> : "Join Citizen Advocates Network for this upcoming session to explore data-driven approaches to responsive governance."}
            </CardDescription>
          </CardContent>
          
          <CardFooter className="pt-4 border-t border-border/50 mt-auto">
            <Link to={post.link || "#"} className="w-full">
              <Button variant="default" className="w-full rounded-sm group">
                Register Now
                <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </CardFooter>
        </Card>)}
    </div>;
}
export default function UpcomingEvents() {
  const wp_query_params = {
    post_type: "event",
    per_page: 3,
    orderby: "date",
    order: "asc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return;
}